import { errorAlert, successAlert } from "@app/utils/alerts";
import {
  deleteData,
  getData,
  postData,
  putData,
} from "@services/utils/formHelpers";
import { create } from "zustand";
import {
  IFormFilterTransitDose,
  PayloadTransitDose,
  TransitDoseListType,
} from "@app/types/transitDose.types";
import { filterTransit, getTop3ById } from "./helpers";

type useTransitDosesType = {
  transitDosesList: TransitDoseListType[];
  transitFiltered: TransitDoseListType[];
  preloadTransitDoses: TransitDoseListType[];
  get: (id: TransitDoseListType["id"]) => TransitDoseListType | undefined;
  query: IFormFilterTransitDose;
  setQuery: (query: IFormFilterTransitDose) => void;
  fetchAll: () => void;
  fetchByInstalacion: (instalacionId: number, idDoses?: number) => void;
  fetchReferenceValues: (instalacionId: number, type: string) => Promise<any[]>;
  loading: boolean;
  loadingFiltered: boolean;
  loadingSave: boolean;
  loadingReferenceValues: boolean;
  save: (data: PayloadTransitDose) => Promise<boolean>;
  filteredTransitDoses: () => TransitDoseListType[];
  delete: (id: number) => void;
};

export const useTransitDose = create<useTransitDosesType>((set, get) => ({
  loading: false,
  loadingSave: false,
  loadingFiltered: false,
  loadingReferenceValues: false,
  transitDosesList: [],
  transitFiltered: [],
  preloadTransitDoses: [],
  query: {},
  setQuery: (query) => set({ query }),
  filteredTransitDoses: () =>
    filterTransit(get().query, get().transitDosesList),
  get: (id) => get().transitDosesList.find((fondo) => fondo.id === id),
  fetchAll: async () => {
    set({ loading: true, preloadTransitDoses: [] });
    try {
      const endpoint = "/transit-doses";
      const { data: response } = await getData(endpoint);

      set(() => ({
        transitDosesList: response.sort(
          (a: TransitDoseListType, b: TransitDoseListType) =>
            (b.id as number) - (a.id as number)
        ),
        loading: false,
      }));
    } catch (error) {
      console.error("Error fetching useTransitDose:", error);
      set({ loading: false });
    }
  },
  fetchByInstalacion: async (instalacionId: number, idDoses?: number) => {
    set({ loadingFiltered: true, preloadTransitDoses: [] });
    try {
      const endpoint = `/transit-doses/filtered?&instalation=${instalacionId}`;
      const { data: response } = await getData(endpoint);

      set(() => ({
        preloadTransitDoses: getTop3ById(
          response.filter((f) => f.id !== idDoses)
        ),
        loadingFiltered: false,
      }));
    } catch (error) {
      console.error("Error transitFiltered useTransitDose:", error);
      set({ loadingFiltered: false });
    }
  },
  fetchReferenceValues: async (instalacionId, type) => {
    set({ loadingReferenceValues: true });
    try {
      const endpoint = "/transit_dose/filtered/" + instalacionId + "/" + type;
      const { data: response } = await getData(endpoint);
      return response;
    } catch (error) {
      console.error("Error fetching reference values:", error);
      set({ loadingReferenceValues: false });
    } finally {
      set({ loadingReferenceValues: false });
    }
  },
  save: async (data: PayloadTransitDose) => {
    set({ loadingSave: true });
    let hasErrors = false;

    try {
      const { id, ...rest } = data;
      const isNew = !id;
      const endpoint = `/transit-doses/${isNew ? "create" : id + "/edit"}`;

      const response = isNew
        ? await postData(endpoint, data)
        : await putData(endpoint, { ...rest });

      hasErrors = !response.success;
      if (hasErrors) {
        errorAlert("Error al guardar el fondo");
      } else successAlert("Dosis de tránsito guardada correctamente");

      return hasErrors;
    } catch (error) {
      errorAlert("Error al guardar el fondo");
      hasErrors = true;
      return hasErrors;
    } finally {
      set({ loadingSave: false });
      !hasErrors && get().fetchAll();
    }
  },
  delete: async (id: number) => {
    set({ loading: true });
    try {
      const endpoint = `/transit-doses/${id}`;
      await deleteData(endpoint);
      successAlert("Dosis de tránsito eliminada correctamente");
      get().fetchAll();
    } catch (error) {
      errorAlert("Error al eliminar la dosis de tránsito");
    } finally {
      set({ loading: false });
    }
  },
}));
